import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { X, FileText } from 'lucide-react';
import AutoTranslatedText from './AutoTranslatedText';
import { useAutoTranslate } from '../hooks/useAutoTranslate';

/**
 * ReadMoreModal - A modal component to display full description text with auto-translation
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {string} title - The title of the content
 * @param {object} titleTranslations - Title translations object
 * @param {string} description - The default/English description
 * @param {object} descriptionTranslations - Description translations object
 * @param {string} subtitle - Optional subtitle (e.g., company slogan)
 * @param {object} subtitleTranslations - Subtitle translations
 * @param {string} imageUrl - Optional image URL
 * @param {boolean} autoTranslate - Whether to auto-translate content (default: true)
 */
const ReadMoreModal = ({
  open,
  onClose,
  title,
  titleTranslations,
  description,
  descriptionTranslations,
  subtitle,
  subtitleTranslations,
  imageUrl,
  autoTranslate = true,
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  // Use auto-translation hooks
  const { translatedText: translatedTitle } = useAutoTranslate(
    title,
    'en',
    currentLang,
    autoTranslate ? titleTranslations : null
  );

  const { translatedText: translatedDescription, isLoading: descLoading } = useAutoTranslate(
    description,
    'en',
    currentLang,
    autoTranslate ? descriptionTranslations : null
  );

  const { translatedText: translatedSubtitle } = useAutoTranslate(
    subtitle,
    'en',
    currentLang,
    autoTranslate ? subtitleTranslations : null
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-5 w-5 text-primary" />
            {translatedTitle}
          </DialogTitle>
          {translatedSubtitle && (
            <DialogDescription className="text-base italic text-slate-600">
              &ldquo;{translatedSubtitle}&rdquo;
            </DialogDescription>
          )}
        </DialogHeader>
        
        {imageUrl && (
          <div className="w-full h-48 rounded-lg overflow-hidden mb-4">
            <img 
              src={imageUrl} 
              alt={translatedTitle}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="prose prose-slate max-w-none">
          {descLoading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <span className="animate-pulse">Translating content...</span>
            </div>
          ) : (
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
              {translatedDescription}
            </p>
          )}
        </div>
        
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * TruncatedDescription - A component that shows truncated text with "Read More" button
 * Now with auto-translation support
 */
export const TruncatedDescription = ({
  description,
  descriptionTranslations,
  title,
  titleTranslations,
  subtitle,
  subtitleTranslations,
  imageUrl,
  maxLength = 150,
  className = '',
  autoTranslate = true,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language || 'en').split('@')[0];

  // Fallback translations for "Read More" in case i18n isn't ready
  const readMoreFallback = {
    en: 'Read More',
    fr: 'Lire la suite',
    es: 'Leer más',
    de: 'Mehr lesen'
  };
  
  const translatingFallback = {
    en: 'Translating...',
    fr: 'Traduction en cours...',
    es: 'Traduciendo...',
    de: 'Übersetzen...'
  };

  const getReadMoreText = () => {
    const translated = t('common.read_more');
    if (translated === 'common.read_more') {
      return readMoreFallback[currentLang] || readMoreFallback.en;
    }
    return translated;
  };

  const getTranslatingText = () => {
    const translated = t('common.translating');
    if (translated === 'common.translating') {
      return translatingFallback[currentLang] || translatingFallback.en;
    }
    return translated;
  };

  const { translatedText, isLoading } = useAutoTranslate(
    description,
    'en',
    currentLang,
    autoTranslate ? descriptionTranslations : null
  );

  const needsTruncation = translatedText && translatedText.length > maxLength;
  const displayText = needsTruncation 
    ? translatedText.substring(0, maxLength) + '...'
    : translatedText;

  if (isLoading) {
    return (
      <div className={className}>
        <p className="text-slate-400 animate-pulse">{getTranslatingText()}</p>
      </div>
    );
  }

  return (
    <>
      <div className={className}>
        <p className="text-slate-600">{displayText}</p>
        {needsTruncation && (
          <button
            onClick={() => setShowModal(true)}
            className="text-primary hover:text-primary/80 text-sm font-medium mt-1 inline-flex items-center gap-1"
          >
            {getReadMoreText()} →
          </button>
        )}
      </div>
      
      <ReadMoreModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={title}
        titleTranslations={titleTranslations}
        description={description}
        descriptionTranslations={descriptionTranslations}
        subtitle={subtitle}
        subtitleTranslations={subtitleTranslations}
        imageUrl={imageUrl}
        autoTranslate={autoTranslate}
      />
    </>
  );
};

export default ReadMoreModal;
